import { useEffect, useMemo } from "react";
import { useQuery } from "@apollo/client";
import type { Store } from "../../../__generated__/graphql";
import type { StoresV2Query, StoresV2QueryVariables } from "../../../__generated__/graphql";
import { GET_STORES } from "../../../api/store-list-queries";
import { useInfiniteScroll } from "./use-infinite-scroll";
import {
  STORE_FILTER,
  filterByPickupTime,
} from "../utils/store-filter";
import type { PickupTimeFilter } from "./use-pickup-time-filter";

type AvailableFilter = "ALL" | "AVAILABLE" | "PICKUP_NOW";

interface RestoredStoresData {
  stores: unknown[];
  offset: number;
  hasMore: boolean;
}

export interface UseHomeStoreListParams {
  restoredData: RestoredStoresData | null;
  latNum: number | null;
  lngNum: number | null;
  hasLastLo: boolean;
  getLocationFromStorage: () => { lat: number; lng: number };
  loadAddr: string | null;
  limit: number;
  isList: boolean;
  availableFilter: AvailableFilter;
  pickupNowStores: Store[] | null;
  pickupTimeFilter: PickupTimeFilter | null;
}

export function useHomeStoreList({
  restoredData,
  latNum,
  lngNum,
  hasLastLo,
  getLocationFromStorage,
  loadAddr,
  limit,
  isList,
  availableFilter,
  pickupNowStores,
  pickupTimeFilter,
}: UseHomeStoreListParams) {
  const queryVariables = useMemo<StoresV2QueryVariables>(
    () => ({
      lat: latNum ?? undefined,
      lng: lngNum ?? undefined,
      offset: 0,
      limit,
    }),
    [latNum, lngNum, limit],
  );

  const { fetchMore, data: queryData } = useQuery<
    StoresV2Query,
    StoresV2QueryVariables
  >(GET_STORES, {
    variables: queryVariables,
    skip: !!restoredData,
  });

  const {
    rootRef,
    loadMoreRef,
    allStores,
    setAllStores,
    setOffset,
    hasMore,
    setHasMore,
    isLoadingMore,
    initLoading,
    setInitLoading,
  } = useInfiniteScroll({
    limit,
    lat: latNum,
    lng: lngNum,
    hasLastLo,
    getLocationFromStorage,
    fetchMore,
    isList,
  });

  // 쿼리 결과를 훅 상태와 동기화 (복원 데이터가 없을 때만) + localStorage 동기화
  useEffect(() => {
    if (!restoredData && queryData?.storesV2) {
      if (queryData.storesV2.length > 0) {
        setAllStores(queryData.storesV2);
        setHasMore(queryData.storesV2.length === limit);
        setOffset(queryData.storesV2.length);
        setInitLoading(false);

        if (latNum != null && lngNum != null) {
          localStorage.setItem("lat", String(latNum));
          localStorage.setItem("lng", String(lngNum));
          if (loadAddr) {
            localStorage.setItem("loadAddr", loadAddr);
          }
        }
      } else {
        setAllStores([]);
        setHasMore(false);
        setInitLoading(false);
      }
    }
  }, [
    queryData,
    restoredData,
    limit,
    latNum,
    lngNum,
    loadAddr,
    setAllStores,
    setHasMore,
    setOffset,
    setInitLoading,
  ]);

  const hasStores = Boolean(allStores && allStores.length > 0);

  const filterByPickupTimeMemo = useMemo(
    () => (store: Store | null) => filterByPickupTime(store, pickupTimeFilter),
    [pickupTimeFilter],
  );

  const filteredStores = useMemo(() => {
    let stores: (Store | null)[] = [];

    if (availableFilter === "PICKUP_NOW") {
      stores = (pickupNowStores || []) as (Store | null)[];
    } else {
      const filterFn = STORE_FILTER[availableFilter];
      stores =
        (allStores?.filter((store) => {
          if (!store) return false;
          return filterFn(store);
        }) || []) as (Store | null)[];
    }

    return stores.filter((store) => {
      if (!store) return false;
      return filterByPickupTimeMemo(store);
    }) as Store[];
  }, [availableFilter, allStores, pickupNowStores, filterByPickupTimeMemo]);

  return {
    rootRef,
    loadMoreRef,
    allStores,
    hasMore,
    isLoadingMore,
    initLoading,
    setInitLoading,
    filteredStores,
    hasStores,
  };
}
